import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { Role, RoleDetail, RoleFormValues, RoleUser, PermissionsList } from "../models/role"
import { RoleDetailSchema, PermissionsListSchema, RoleUserSchema } from "../models/role"
import { roleApi } from "../services/roleApi"

const BASE_KEY = "admin-roles"
const PERM_KEY = "admin-permissions"

export function useRole(name?: string | null) {
  return useQuery<RoleDetail>({
    queryKey: [BASE_KEY, name],
    queryFn: () => roleApi.getByName(name!) as Promise<RoleDetail>,
    enabled: !!name,
    staleTime: 1000 * 60 * 5,
  })
}

export function useCreateRole() {
  const queryClient = useQueryClient()
  return useMutation<Role, Error, RoleFormValues>({
    mutationFn: (values) => roleApi.create(values) as Promise<Role>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useUpdateRole() {
  const queryClient = useQueryClient()
  return useMutation<Role, Error, { name: string; values: Partial<RoleFormValues> }>({
    mutationFn: ({ name, values }) => roleApi.update(name, values) as Promise<Role>,
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
      queryClient.invalidateQueries({ queryKey: [BASE_KEY, vars.name] })
    },
  })
}

export function useDeleteRole() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (name) => roleApi.delete(name) as Promise<void>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useRolePermissions(name?: string | null) {
  return useQuery<{ assigned: string[]; available: string[] }>({
    queryKey: [BASE_KEY, "permissions", name],
    queryFn: () => roleApi.getPermissions(name!) as Promise<{ assigned: string[]; available: string[] }>,
    enabled: !!name,
    staleTime: 1000 * 60 * 2,
  })
}

export function useAssignRolePermissions() {
  const queryClient = useQueryClient()
  return useMutation<{ assigned: string[] }, Error, { name: string; add: string[]; remove: string[] }>({
    mutationFn: ({ name, add, remove }) => roleApi.assignPermissions(name, { add, remove }) as Promise<{ assigned: string[] }>,
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY, "permissions", vars.name] })
      queryClient.invalidateQueries({ queryKey: [BASE_KEY, vars.name] })
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useRoleUsers(name?: string | null, params?: Record<string, unknown>) {
  return useQuery<{ items: RoleUser[]; total: number }>({
    queryKey: [BASE_KEY, "users", name, params],
    queryFn: () => roleApi.getUsers(name!, params) as Promise<{ items: RoleUser[]; total: number }>,
    enabled: !!name,
    staleTime: 1000 * 60 * 2,
  })
}

export function useAssignRoleUsers() {
  const queryClient = useQueryClient()
  return useMutation<{ user_ids: number[]; user_count: number }, Error, { name: string; add: number[]; remove: number[] }>({
    mutationFn: ({ name, add, remove }) => roleApi.assignUsers(name, { add, remove }) as Promise<{ user_ids: number[]; user_count: number }>,
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY, "users", vars.name] })
      queryClient.invalidateQueries({ queryKey: [BASE_KEY, vars.name] })
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function usePermissionsList() {
  return useQuery<PermissionsList>({
    queryKey: [PERM_KEY, "all"],
    queryFn: () => roleApi.getAllPermissions() as Promise<PermissionsList>,
    staleTime: 1000 * 60 * 10,
  })
}
